/*
 * Akva — ⓘ-forklaring (snippets/akva-forklaring.liquid).
 *
 * Et lille ⓘ med en faktaboks. Boksen er åben, når en af tre ting gælder:
 *   · musen står over ⓘ eller boksen (ikke touch, det giver falske hover),
 *   · ⓘ har tastaturfokus (:focus-visible, ikke fokus fra et klik),
 *   · den er fastgjort med et klik eller tryk.
 * Et klik på ⓘ fastgør boksen (også når den allerede var åben af hover),
 * og et klik mere lukker den. Et tryk på selve boksen lukker den også, med
 * mindre det er et link i teksten. Esc lukker, og musen skal ud og ind igen,
 * før hover åbner den på ny. Et tryk udenfor eller fokus videre til et andet
 * element lukker en fastgjort boks. Kun én boks er åben ad gangen.
 *
 * Når boksen åbner, måles den og flyttes vandret ind på skærmen
 * (--akva-fk-dx), så en boks ved en overskrift langt til højre ikke stikker
 * ud på en telefon.
 */

/** @type {AkvaForklaring | null} */
let current = null;

const MARGIN = 16;

class AkvaForklaring extends HTMLElement {
  #hover = false;
  #focus = false;
  #pinned = false;
  #dismissed = false;
  #timer = 0;

  connectedCallback() {
    this.button = this.querySelector('.akva-fk__i');
    this.box = this.querySelector('.akva-fk__box');
    if (!(this.button instanceof HTMLButtonElement) || !(this.box instanceof HTMLElement)) return;

    this.addEventListener('pointerenter', this.#onEnter);
    this.addEventListener('pointerleave', this.#onLeave);
    this.addEventListener('focusin', this.#onFocusIn);
    this.addEventListener('focusout', this.#onFocusOut);
    this.button.addEventListener('click', this.#onClick);
    this.box.addEventListener('click', this.#onBoxClick);
  }

  disconnectedCallback() {
    this.removeEventListener('pointerenter', this.#onEnter);
    this.removeEventListener('pointerleave', this.#onLeave);
    this.removeEventListener('focusin', this.#onFocusIn);
    this.removeEventListener('focusout', this.#onFocusOut);
    this.button?.removeEventListener('click', this.#onClick);
    this.box?.removeEventListener('click', this.#onBoxClick);
    clearTimeout(this.#timer);
    this.#listen(false);
    if (current === this) current = null;
  }

  get open() {
    return this.hasAttribute('data-open');
  }

  /** Luk udefra (fx når en anden boks åbner). */
  close() {
    this.#hover = false;
    this.#focus = false;
    this.#pinned = false;
    this.#render();
  }

  /* ── Hændelser ─────────────────────────────────────────────────────── */

  /** @param {PointerEvent} event */
  #onEnter = (event) => {
    if (event.pointerType !== 'mouse') return;
    clearTimeout(this.#timer);
    this.#hover = true;
    this.#render();
  };

  /** @param {PointerEvent} event */
  #onLeave = (event) => {
    if (event.pointerType !== 'mouse') return;
    clearTimeout(this.#timer);
    // Lidt tålmodighed, så musen kan nå fra ⓘ ned på boksen.
    this.#timer = window.setTimeout(() => {
      this.#hover = false;
      this.#dismissed = false;
      this.#render();
    }, 150);
  };

  #onFocusIn = () => {
    if (!this.button?.matches(':focus-visible')) return;
    this.#focus = true;
    this.#render();
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    const next = event.relatedTarget;
    if (next instanceof Node && this.contains(next)) return;
    this.#focus = false;
    // Fokus videre til et andet element (Tab) lukker også en fastgjort boks.
    // Uden et nyt element er det et klik, og så afgør klikket udenfor.
    if (next) {
      this.#pinned = false;
      this.#dismissed = false;
    }
    this.#render();
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    event.preventDefault();
    if (this.#pinned && this.open) {
      this.#pinned = false;
      this.#dismissed = true;
    } else {
      this.#pinned = true;
      this.#dismissed = false;
    }
    this.#render();
  };

  /** @param {MouseEvent} event */
  #onBoxClick = (event) => {
    if (event.target instanceof Element && event.target.closest('a')) return;
    this.#pinned = false;
    this.#dismissed = true;
    this.#render();
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'Escape' || !this.open) return;
    event.preventDefault();
    this.#pinned = false;
    this.#dismissed = true;
    this.#render();
  };

  /** @param {PointerEvent} event */
  #onOutside = (event) => {
    if (event.target instanceof Node && this.contains(event.target)) return;
    this.#pinned = false;
    this.#render();
  };

  #onResize = () => this.#place();

  /* ── Tilstand ──────────────────────────────────────────────────────── */

  #render() {
    const open = !this.#dismissed && (this.#hover || this.#focus || this.#pinned);
    if (open === this.open) return;

    this.toggleAttribute('data-open', open);
    this.button?.setAttribute('aria-expanded', String(open));
    this.#listen(open);

    if (open) {
      if (current && current !== this) current.close();
      current = this;
      this.#place();
    } else if (current === this) {
      current = null;
    }
  }

  /** @param {boolean} on */
  #listen(on) {
    const method = on ? 'addEventListener' : 'removeEventListener';
    document[method]('keydown', this.#onKeydown, true);
    document[method]('pointerdown', this.#onOutside, true);
    window[method]('resize', this.#onResize);
  }

  #place() {
    if (!(this.box instanceof HTMLElement)) return;
    this.style.setProperty('--akva-fk-dx', '0px');
    const box = this.box.getBoundingClientRect();
    const width = document.documentElement.clientWidth;
    let dx = 0;
    if (box.right > width - MARGIN) dx = width - MARGIN - box.right;
    if (box.left + dx < MARGIN) dx = MARGIN - box.left;
    this.style.setProperty('--akva-fk-dx', `${Math.round(dx)}px`);
  }
}

if (!customElements.get('akva-forklaring')) {
  customElements.define('akva-forklaring', AkvaForklaring);
}
